import { useState } from "react";

import { useIdentities, useIdentity, useUpdateEngagement } from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import { Button, Empty, Field, Input, humanStatus } from "src/components/ui";
import type { IdentityFieldsFragment } from "src/graphql/generated";
import { personDisplayName } from "src/lib/names";
import { canWrite, type Viewer } from "src/screens/types";

const pageSize = 20;
type Organization = { id: string; name: string };

export function AddOrgPerson({
  viewer,
  organization,
  onAdded
}: {
  viewer: Viewer;
  organization: Organization;
  onAdded: () => void;
}) {
  if (!canWrite(viewer, "PARTNER")) {
    return <Empty>Adding someone to an organization requires write access to partners.</Empty>;
  }
  return <PersonPicker organization={organization} onAdded={onAdded} />;
}

function PersonPicker({
  organization,
  onAdded
}: {
  organization: Organization;
  onAdded: () => void;
}) {
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [identityId, setIdentityId] = useState<string>();
  const { data, isPending, isFetching, error, refetch } = useIdentities({
    populations: ["PARTNER"],
    search: search.trim() || undefined,
    limit: pageSize + 1,
    offset
  });
  const selected = useIdentity(identityId);
  const rows = data?.identities.slice(0, pageSize) ?? [];
  const hasNext = (data?.identities.length ?? 0) > pageSize;

  if (identityId) {
    if (selected.data?.identity && !selected.error) {
      return (
        <AssignEngagement
          key={identityId}
          person={selected.data.identity}
          organization={organization}
          onAdded={onAdded}
          onBack={() => setIdentityId(undefined)}
        />
      );
    }
    return (
      <div className="space-y-4">
        <Button variant="secondary" onClick={() => setIdentityId(undefined)}>
          Choose another person
        </Button>
        {selected.isPending ? <Empty>Loading person…</Empty> : null}
        {selected.error ? <ReadFailure error={selected.error} /> : null}
        {selected.error ? (
          <Button variant="secondary" onClick={() => selected.refetch()}>
            Retry
          </Button>
        ) : null}
        {!selected.isPending && !selected.error ? (
          <Empty>This person is no longer available. Choose another person.</Empty>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Choose an existing partner to add to {organization.name}.
      </p>
      <Field label="Search people" hint="Names and emails">
        <Input
          value={search}
          placeholder="name or email"
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
      </Field>
      {error ? <ReadFailure error={error} /> : null}
      {isPending ? <Empty>Loading…</Empty> : null}
      {!isPending && !error && rows.length === 0 ? (
        <Empty>No partners match. Try another name or email.</Empty>
      ) : null}
      <ul className="divide-y divide-slate-100">
        {rows.map((person) => (
          <li key={person.id}>
            <button
              type="button"
              className="w-full rounded px-2 py-2 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-500"
              onClick={() => setIdentityId(person.id)}
            >
              <span className="block font-medium">{personDisplayName(person)}</span>
              <span className="block text-xs text-slate-600">
                {[person.workEmail, person.personalEmail].filter(Boolean).join(" · ")}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div
        role="group"
        aria-label="Partner search pagination"
        className="flex items-center justify-between gap-3"
      >
        <p aria-live="polite" className="text-xs text-slate-600">
          Page {offset / pageSize + 1}
          {isFetching ? " · Loading…" : ""}
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset(offset - pageSize)}
          >
            Previous
          </Button>
          {error ? (
            <Button variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={!hasNext || isFetching || Boolean(error)}
            onClick={() => setOffset(offset + pageSize)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function AssignEngagement({
  person,
  organization,
  onAdded,
  onBack
}: {
  person: IdentityFieldsFragment;
  organization: Organization;
  onAdded: () => void;
  onBack: () => void;
}) {
  const terms = person.engagements.filter((term) => term.population === "PARTNER");
  // A lapsed but unclosed term also reads ENDED. Historical assignments should
  // always be deliberate, so isCurrent alone is not enough to preselect it.
  const current = terms.filter((term) => term.isCurrent && term.status !== "ENDED");
  const [engagementId, setEngagementId] = useState(
    current.length === 1 ? current[0]?.id : undefined
  );
  const update = useUpdateEngagement();
  const term = terms.find((candidate) => candidate.id === engagementId);
  const alreadyAdded = term?.partnerOrganization?.id === organization.id;
  const previousOrganization = term?.partnerOrganization;

  return (
    <form
      className="space-y-4"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!term || alreadyAdded || update.isPending) {
          return;
        }
        try {
          await update.mutateAsync({
            identityId: person.id,
            engagementId: term.id,
            fields: { partnerOrganizationId: organization.id }
          });
          onAdded();
        } catch {
          // The mutation reports the failure; keep the selection available to retry.
        }
      }}
    >
      <Button variant="secondary" disabled={update.isPending} onClick={onBack}>
        Choose another person
      </Button>
      <div>
        <p className="font-medium">{personDisplayName(person)}</p>
        <p className="text-sm text-slate-600">{person.workEmail ?? person.personalEmail}</p>
      </div>
      {terms.length === 0 ? (
        <Empty>This person has no partner engagement to add to an organization.</Empty>
      ) : (
        <fieldset disabled={update.isPending} className="space-y-2">
          <legend className="mb-2 text-sm font-medium">Partner engagement</legend>
          {terms.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-start gap-2 rounded border border-slate-200 p-3 text-sm"
            >
              <input
                type="radio"
                name="engagement"
                value={candidate.id}
                checked={engagementId === candidate.id}
                onChange={() => setEngagementId(candidate.id)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">
                  {candidate.title} · {humanStatus(candidate.status)}
                </span>
                <span className="block text-xs text-slate-600">
                  {candidate.startDate ?? "No start date"} –{" "}
                  {candidate.effectiveEndDate ?? "No end date"}
                </span>
                <span className="block text-xs text-slate-600">
                  {candidate.partnerOrganization?.name ?? "No organization"}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {alreadyAdded ? (
        <p className="text-sm text-slate-600">This engagement is already at {organization.name}.</p>
      ) : null}
      {!alreadyAdded && previousOrganization ? (
        <p className="text-sm text-amber-800">
          This will move this engagement from {previousOrganization.name} to {organization.name}.
        </p>
      ) : null}
      {term?.status === "ENDED" && !alreadyAdded ? (
        <p className="text-sm text-amber-800">
          This engagement has ended. Its organization will change; its dates and status will stay
          the same.
        </p>
      ) : null}
      {update.error ? (
        <p role="alert" className="text-sm text-red-700">
          {update.error.message}
        </p>
      ) : null}
      <Button type="submit" disabled={!term || alreadyAdded || update.isPending}>
        {update.isPending
          ? "Saving…"
          : previousOrganization && !alreadyAdded
            ? "Move to organization"
            : "Add to organization"}
      </Button>
    </form>
  );
}
